'use client'

import { useEffect, useRef, useState } from 'react'
import { createShardMap, applyShardsToGrid } from './shards/grid'
import type { GridData, ImageDimensions, PixelationState, PixelationStep } from './lib/types'
import { createGridFromImage } from './grid/utils'
import { applyShuffledRegions, createRegionMapFromParams, shuffleRegions } from './grid/regions'
import { animateRegionShuffle } from './animate/regionShuffle'
import { renderGridToCanvas } from './grid/utils'
import { pixelateGrid } from './grid/pixelate'
import { animateRandomPixelationTransition } from './animate/pixelate'
import { orderedSteps } from './lib/types'
import { computeSaturatedGrid } from './grid/saturate'
import { animateZoomWithPixelation } from './animate/zoom'
import { computeZoomedGrid } from './grid/zoom'
import { computeSaturatedShardedGrids } from './grid/hiddenShards'
import { animateShardTransition } from './animate/shardTransition'
import { useImage } from './useImage'
import { easeIn } from './animate/easing'
import fallbackImage from './assets/green-field.png'
import { useResponsiveParams } from './useResponsiveParams'
import { updateOliveBackgroundOnly } from './grid/oliveBackground'

const stepCycleSpeed: Record<PixelationStep, number> = {
  original: 200,
  pixelate: 1000,
  shuffle: 200,
  shards: 0,
  saturate: 0,
  'double-density': 0,
  explode: 0,
  zoom: 10_000,
  revert: 0,
}

const stepAnimationDuration: Record<PixelationStep, number> = {
  original: 0,
  pixelate: 2000,
  shuffle: 500,
  shards: 3000,
  saturate: 800,
  'double-density': 800,
  explode: 200,
  zoom: 200,
  revert: 200,
}

export default function usePixelator({ imageUrl }: { imageUrl?: string } = {}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const imageMeta = useImage(containerRef, imageUrl || fallbackImage.src)
  const params = useResponsiveParams(containerRef)
  const blockSize = params?.blockSize ?? 0

  // Refs
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const canvasContainerRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement | null>(null)
  const animationRef = useRef<{ cancel: () => void } | null>(null)
  const autoCycleTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isResettingRef = useRef(false)

  // State
  const [imageDimensions, setImageDimensions] = useState<ImageDimensions>({
    width: 0,
    height: 0,
    x: 0,
    y: 0,
  })
  const [state, setState] = useState<PixelationState>({
    currentStep: 'original',
    isAnimating: false,
    originalGrid: null,
    previousGrid: null,
    zoomGrids: {
      oliveGrid: null,
      colorGrid: null,
    },
    regionMap: null,
    shardMap: null,
  })

  // Load image
  useEffect(() => {
    if (!imageMeta) return

    console.log('loading image')
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      imageRef.current = img
      if (canvasRef.current && canvasContainerRef.current) {
        const { grid, dimensions } = createGridFromImage(
          img,
          canvasRef.current,
          canvasContainerRef.current,
        )
        setImageDimensions(dimensions)
        setState((prev) => ({
          ...prev,
          originalGrid: grid,
          previousGrid: copyGrid(grid),
        }))
        renderGridToCanvas(canvasRef.current, grid, dimensions)
      }
    }
    img.src = imageMeta?.imageUrl

    setState((prev) => ({
      ...prev,
      currentStep: 'original',
      isAnimating: false,
      originalGrid: null,
      previousGrid: null,
    }))
  }, [imageMeta])

  // Handle cycling
  useEffect(() => {
    if (
      state.currentStep !== 'zoom' &&
      !state.isAnimating &&
      // Only start once the grid has been loaded.
      state.originalGrid &&
      state.previousGrid
    ) {
      autoCycleTimeoutRef.current = setTimeout(() => {
        handleNextStep(true)
      }, stepCycleSpeed[state.currentStep])
    }

    return () => {
      if (autoCycleTimeoutRef.current) {
        clearTimeout(autoCycleTimeoutRef.current)
        autoCycleTimeoutRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentStep, state.isAnimating, state.originalGrid, state.previousGrid])

  // Handle step transitions
  const handleNextStep = (isAutoCycle = false) => {
    if (state.isAnimating || !state.originalGrid || !state.previousGrid) {
      return
    }

    const currentIndex = orderedSteps.indexOf(state.currentStep)
    const nextIndex = (currentIndex + 1) % orderedSteps.length
    const nextStep = orderedSteps[nextIndex]

    // Stop at zoom state if not auto-cycling
    if (nextStep === 'zoom' && !isAutoCycle) {
      setState((prev) => ({ ...prev, isAnimating: true }))
      if (canvasRef.current && state.previousGrid) {
        const { grid: zoomedGrid } = computeZoomedGrid(state.previousGrid, imageDimensions)
        if (animationRef.current) {
          animationRef.current.cancel()
        }
        animationRef.current = animateZoomWithPixelation({
          canvas: canvasRef.current,
          startGrid: state.previousGrid,
          targetGrid: zoomedGrid,
          dimensions: imageDimensions,
          blockSize: blockSize * 2,
          durationMs: stepAnimationDuration[nextStep],
          onComplete: () => {
            setState((prev) => ({
              ...prev,
              currentStep: nextStep,
              isAnimating: false,
              previousGrid: zoomedGrid,
            }))
            animationRef.current = null
          },
        })
      }
      return
    }

    console.log(
      `Transitioning from ${state.currentStep} to next step (isAutoCycle: ${isAutoCycle})`,
    )
    setState((prev) => ({ ...prev, isAnimating: true }))

    switch (nextStep) {
      case 'original':
        if (canvasRef.current && state.originalGrid) {
          console.log('Rendering original grid')
          renderGridToCanvas(canvasRef.current, state.originalGrid, imageDimensions)
          setState((prev) => ({
            ...prev,
            currentStep: nextStep,
            isAnimating: false,
            regionMap: null,
            shardMap: null,
          }))
        }
        break

      case 'pixelate':
        if (canvasRef.current && state.originalGrid) {
          console.log('Pixelating grid')
          const pixelatedBlockSize = blockSize * 2
          const pixelatedGrid = pixelateGrid(state.originalGrid, pixelatedBlockSize)

          if (animationRef.current) {
            animationRef.current.cancel()
          }

          animationRef.current = animateRandomPixelationTransition({
            canvas: canvasRef.current,
            startGrid: state.previousGrid,
            targetGrid: pixelatedGrid,
            dimensions: imageDimensions,
            blockSize: pixelatedBlockSize,
            durationMs: stepAnimationDuration[nextStep],
            easing: easeIn,
            onComplete: () => {
              console.log('Pixelation animation complete')
              setState((prev) => ({
                ...prev,
                currentStep: nextStep,
                isAnimating: false,
                previousGrid: pixelatedGrid,
              }))
              animationRef.current = null
            },
          })
        }
        break

      case 'shuffle':
        if (canvasRef.current && state.previousGrid) {
          console.log('Creating and shuffling regions')

          // Create regions
          const regionMap = createRegionMapFromParams(params!)

          // Shuffle regions
          const shuffledRegionMap = shuffleRegions(regionMap)

          if (animationRef.current) {
            animationRef.current.cancel()
          }
          const animationDuration = stepAnimationDuration[nextStep]

          animationRef.current = animateRegionShuffle(
            canvasRef.current,
            state.previousGrid,
            shuffledRegionMap,
            imageDimensions,
            animationDuration,
            () => {
              // Apply shuffled regions to grid
              const shuffledGrid = applyShuffledRegions(state.previousGrid!, shuffledRegionMap)

              // Render the shuffled grid
              renderGridToCanvas(canvasRef.current!, shuffledGrid, imageDimensions)

              setState((prev) => ({
                ...prev,
                currentStep: nextStep,
                isAnimating: false,
                previousGrid: shuffledGrid,
                regionMap: shuffledRegionMap,
              }))

              animationRef.current = null
            },
          )
        }
        break

      case 'shards':
        if (canvasRef.current && state.previousGrid && state.regionMap) {
          console.log('Creating and applying shards')
          const shardBlockSize = blockSize * 2
          // Create shards
          const shardMap = createShardMap(state.regionMap, shardBlockSize)
          console.log(`Created ${shardMap.shards.length} shards`)

          // Apply shards to grid
          const shardedGrid = applyShardsToGrid(state.previousGrid, state.regionMap, shardMap)

          if (animationRef.current) {
            animationRef.current.cancel()
          }

          animationRef.current = animateShardTransition({
            canvas: canvasRef.current,
            startGrid: state.previousGrid,
            shards: shardMap.shards,
            dimensions: imageDimensions,
            blockSize: shardBlockSize,
            regionMap: state.regionMap,
            durationMs: stepAnimationDuration[nextStep],
            onComplete: () => {
              console.log('Shard animation complete')
              setState((prev) => ({
                ...prev,
                currentStep: nextStep,
                isAnimating: false,
                previousGrid: shardedGrid,
                shardMap,
              }))
              animationRef.current = null
            },
          })
        }
        break

      case 'saturate':
        if (canvasRef.current && state.previousGrid) {
          console.log('Saturating grid with colors')
          const saturatedBlockSize = blockSize * 2

          if (animationRef.current) {
            animationRef.current.cancel()
          }

          const saturatedGrid = computeSaturatedGrid(imageDimensions, [], saturatedBlockSize)

          animationRef.current = animateRandomPixelationTransition({
            canvas: canvasRef.current,
            startGrid: state.previousGrid,
            targetGrid: saturatedGrid,
            dimensions: imageDimensions,
            blockSize: saturatedBlockSize,
            durationMs: stepAnimationDuration[nextStep],
            onComplete: () => {
              console.log('Saturation animation complete')
              setState((prev) => ({
                ...prev,
                currentStep: nextStep,
                previousGrid: saturatedGrid,
                isAnimating: false,
              }))
              animationRef.current = null
            },
          })
        } else {
          console.error('Cannot saturate: missing previousGrid')
          setState((prev) => ({ ...prev, isAnimating: false }))
        }
        break

      case 'double-density':
        if (canvasRef.current && state.previousGrid) {
          console.log('Creating double density grid')
          const doubleDensityBlockSize = blockSize

          // Create double density grid
          const doubleDensityGrid = computeSaturatedGrid(
            imageDimensions,
            [],
            doubleDensityBlockSize,
          )

          if (animationRef.current) {
            animationRef.current.cancel()
          }

          animationRef.current = animateRandomPixelationTransition({
            canvas: canvasRef.current,
            startGrid: state.previousGrid,
            targetGrid: doubleDensityGrid,
            dimensions: imageDimensions,
            blockSize: doubleDensityBlockSize,
            durationMs: stepAnimationDuration[nextStep],
            onComplete: () => {
              console.log('Pixelation animation complete')
              setState((prev) => ({
                ...prev,
                currentStep: nextStep,
                isAnimating: false,
                previousGrid: doubleDensityGrid,
              }))
              animationRef.current = null
            },
          })
        }
        break

      case 'explode':
        if (canvasRef.current && state.previousGrid) {
          const explodeBlockSize = blockSize
          console.log('Applying olive effect with shards to all pixels over 2 seconds')

          const { oliveGrid, colorGrid, shardMap } = computeSaturatedShardedGrids(
            imageDimensions,
            [],
            explodeBlockSize,
          )

          if (animationRef.current) {
            animationRef.current.cancel()
          }

          animationRef.current = animateRandomPixelationTransition({
            canvas: canvasRef.current,
            startGrid: state.previousGrid,
            targetGrid: colorGrid,
            dimensions: imageDimensions,
            blockSize: explodeBlockSize,
            durationMs: stepAnimationDuration[nextStep],
            onComplete: () => {
              console.log('Olive animation complete')
              setState((prev) => ({
                ...prev,
                currentStep: nextStep,
                isAnimating: false,
                previousGrid: oliveGrid,
                zoomGrids: {
                  oliveGrid,
                  colorGrid,
                },
                shardMap,
              }))
              animationRef.current = null
            },
          })
        }
        break

      case 'zoom':
        if (canvasRef.current && state.previousGrid && imageDimensions) {
          console.log('Zooming into center quarter of grid with pixelation')

          const { oliveGrid, colorGrid } = state.zoomGrids

          setState((prev) => ({
            ...prev,
            isAnimating: true,
          }))

          animationRef.current = animateZoomWithPixelation({
            canvas: canvasRef.current,
            startGrid: colorGrid!,
            targetGrid: oliveGrid!,
            dimensions: imageDimensions,
            blockSize: blockSize,
            onComplete: () => {
              // Calculate final zoomed state
              const { grid: zoomedGrid } = computeZoomedGrid(oliveGrid!, imageDimensions)

              setState((prev) => ({
                ...prev,
                currentStep: nextStep,
                isAnimating: false,
                previousGrid: zoomedGrid,
              }))
            },
            durationMs: stepAnimationDuration.zoom,
          })
        }
        break

      case 'revert':
        if (canvasRef.current && state.originalGrid && state.previousGrid) {
          const revertBlockSize = blockSize
          console.log('Reverting grid')

          if (animationRef.current) {
            animationRef.current.cancel()
          }

          const originalGrid = copyGrid(state.originalGrid)

          animationRef.current = animateRandomPixelationTransition({
            canvas: canvasRef.current,
            startGrid: state.previousGrid,
            targetGrid: originalGrid,
            dimensions: imageDimensions,
            blockSize: revertBlockSize,
            durationMs: stepAnimationDuration[nextStep],
            onComplete: () => {
              console.log('Revert animation complete')
              setState((prev) => ({
                ...prev,
                currentStep: nextStep,
                isAnimating: false,
                previousGrid: originalGrid,
              }))
              animationRef.current = null
            },
          })
        }
        break
    }
  }

  const handleReset = () => {
    if (!state.originalGrid) return

    if (canvasRef.current) {
      console.log('Resetting grid')
      const copiedOriginalGrid = copyGrid(state.originalGrid)
      console.log('Copied original grid', copiedOriginalGrid)
      renderGridToCanvas(canvasRef.current, copiedOriginalGrid, imageDimensions)
      setState((prev) => ({
        ...prev,
        currentStep: 'original',
        isAnimating: false,
        previousGrid: copiedOriginalGrid,
        regionMap: null,
        shardMap: null,
      }))
    }
  }

  // Animate oliveGrid background during zoom step, keeping shards in place and redrawing the zoomed grid (no animation)
  useEffect(() => {
    if (state.currentStep !== 'zoom' || !imageDimensions || !blockSize) return

    let isActive = true
    const updateOliveBackgroundAndRedraw = () => {
      if (!isActive) return
      setState((prev) => {
        const prevOlive = prev.zoomGrids.oliveGrid
        if (!prevOlive) return prev
        const newOlive = updateOliveBackgroundOnly(prevOlive, imageDimensions, blockSize)
        // Compute the zoomed-in grid and redraw instantly
        if (canvasRef.current) {
          const { grid: zoomedGrid } = computeZoomedGrid(newOlive, imageDimensions)
          renderGridToCanvas(canvasRef.current, zoomedGrid, imageDimensions)
        }
        return {
          ...prev,
          zoomGrids: {
            ...prev.zoomGrids,
            oliveGrid: newOlive,
          },
        }
      })
    }

    const interval = setInterval(updateOliveBackgroundAndRedraw, 1000)
    return () => {
      isActive = false
      clearInterval(interval)
    }
  }, [state.currentStep, imageDimensions, blockSize])

  // Reset everything when container width changes
  useEffect(() => {
    const node = canvasContainerRef.current
    if (!node) return

    let lastWidth = node.clientWidth

    function handleResize() {
      if (!node || isResettingRef.current) return
      const currentWidth = node.clientWidth
      if (currentWidth !== lastWidth) {
        console.log('Width changed:', { lastWidth, currentWidth })
        lastWidth = currentWidth

        // Clear any existing timeout
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current)
          debounceTimeoutRef.current = null
        }

        // Set new timeout
        debounceTimeoutRef.current = setTimeout(() => {
          console.log('Debounce timeout triggered, resetting...')
          isResettingRef.current = true

          // Clean up any existing animations
          if (animationRef.current) {
            animationRef.current.cancel()
            animationRef.current = null
          }

          // Clean up any existing intervals
          if (autoCycleTimeoutRef.current) {
            clearTimeout(autoCycleTimeoutRef.current)
            autoCycleTimeoutRef.current = null
          }

          // Reset the state and grid
          handleReset()

          // Reset the flag after a short delay to ensure all cleanup is complete
          setTimeout(() => {
            isResettingRef.current = false
          }, 100)
        }, 1000)
      }
    }

    const resizeObserver = new window.ResizeObserver(handleResize)
    resizeObserver.observe(node)
    window.addEventListener('resize', handleResize)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', handleResize)
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
        debounceTimeoutRef.current = null
      }
      isResettingRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasContainerRef])

  return {
    params,
    state,
    handleNextStep,
    handleReset,
    containerRef,
    canvasContainerRef,
    canvasRef,
    imageRef,
    imageMeta,
  }
}

function copyGrid(grid: GridData): GridData {
  return {
    pixels: grid.pixels.map((row) => row.map((pixel) => ({ ...pixel }))),
    width: grid.width,
    height: grid.height,
  }
}
